import { Helmet } from "react-helmet-async";

interface FAQItem {
  question: string;
  answer: string;
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface StructuredDataProps {
  type: 'faq' | 'product' | 'organization' | 'localBusiness' | 'article' | 'breadcrumb';
  data?: {
    faqItems?: FAQItem[];
    productName?: string;
    productDescription?: string;
    articleTitle?: string;
    articleAuthor?: string;
    articlePublished?: string;
    articleImage?: string;
    breadcrumbItems?: BreadcrumbItem[];
  };
}

const StructuredData = ({ type, data }: StructuredDataProps) => {
  const generateSchema = () => {
    switch (type) {
      case 'faq':
        return {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": data?.faqItems?.map(item => ({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.answer
            }
          }))
        };
      
      case 'product':
        return {
          "@context": "https://schema.org",
          "@type": "Product",
          "name": data?.productName || "HostCall.ai",
          "description": data?.productDescription || "AI-powered guest communication platform for hotels and vacation rentals",
          "brand": {
            "@type": "Brand",
            "name": "HostCall.ai"
          },
          "offers": {
            "@type": "AggregateOffer",
            "priceCurrency": "USD",
            "lowPrice": "0",
            "highPrice": "499",
            "offerCount": "3"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "reviewCount": "200"
          }
        };
      
      case 'organization':
        return {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "HostCall.ai",
          "url": "https://hostcall.ai",
          "logo": "https://hostcall.ai/HostCall-ai-logo-v3-transp.png",
          "description": "AI-powered guest communication platform for hotels and vacation rentals",
          "sameAs": ["https://linkedin.com/company/hostcall-ai"],
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+46-76-692-89-01",
            "contactType": "sales",
            "availableLanguage": ["English", "Swedish"]
          }
        };

      case 'localBusiness':
        return {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://hostcall.ai/#localbusiness",
          "name": "HostCall.ai",
          "url": "https://hostcall.ai",
          "logo": "https://hostcall.ai/HostCall-ai-logo-v3-transp.png",
          "image": "https://hostcall.ai/HostCall-ai-logo-v3-transp.png",
          "telephone": "+46-76-692-89-01",
          "email": "hello@hostcall.ai",
          "priceRange": "$$",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Kungsgatan 8",
            "addressLocality": "Stockholm",
            "postalCode": "111 43",
            "addressCountry": "SE"
          },
          "openingHoursSpecification": [{
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "09:00",
            "closes": "18:00"
          }],
          "sameAs": ["https://linkedin.com/company/hostcall-ai"]
        };


      
      case 'article':
        return {
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": data?.articleTitle,
          "author": {
            "@type": "Person",
            "name": data?.articleAuthor
          },
          "datePublished": data?.articlePublished,
          "image": data?.articleImage || "https://hostcall.ai/HostCall-ai-logo-v3-transp.png",
          "publisher": {
            "@type": "Organization",
            "name": "HostCall.ai",
            "logo": {
              "@type": "ImageObject",
              "url": "https://hostcall.ai/HostCall-ai-logo-v3-transp.png"
            }
          }
        };
      
      case 'breadcrumb':
        return {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": data?.breadcrumbItems?.map((item, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": item.name,
            "item": item.url
          }))
        };
      
      default:
        return null;
    }
  };

  const schema = generateSchema();
  
  if (!schema) return null;

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export default StructuredData;
